import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

/**
 * Widget que muestra las CAPACITACIONES y AUTOCAPACITACIONES activas del semestre
 * en las que el profesor (o admin) está INSCRIPTO como alumno.
 *
 * Usa el mismo endpoint que el dashboard (`/api/profesor/mi-clase-hoy`) y filtra a
 * `tipo_participacion === 'estudiante'` + `categoria` en (`profesores`, `autocapacitacion`).
 *
 * Diseño visual replicado del `MiClaseHoyWidget` del Dashboard (ClaseCard verde),
 * pero más compacto para usar dentro de la Sala Docente.
 */
const CapacitacionesProfesorWidget = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // El widget aplica a PROFESORES (la audiencia explícita pedida) y opcionalmente
    // a admins inscriptos también, para que puedan probar el flujo.
    if (!user || !['profesor', 'admin'].includes(user.rol)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/profesor/mi-clase-hoy`);
        if (cancelled) return;
        const all = r?.data?.clases || [];
        // Capacitaciones donde el usuario está INSCRIPTO. Para profesores el endpoint
        // marca `tipo_participacion === 'estudiante'` cuando están inscriptos.
        // Para admins marca 'admin' siempre, así que filtramos por categoría +
        // presencia en estudiantes_inscritos (el endpoint ya solo devuelve activas).
        const myId = user.id;
        const filtered = all.filter((c) => {
          const cat = (c.categoria || '').toLowerCase();
          const esCapacitacion = cat === 'profesores' || cat === 'autocapacitacion' || cat === 'autocapacitacion_profesores';
          if (!esCapacitacion) return false;
          // 1) Usuario inscripto como alumno
          if (c.tipo_participacion === 'estudiante') return true;
          // 2) Usuario asignado como capacitador (profesor_id de la clase)
          if (c.tipo_participacion === 'profesor') return true;
          // 3) Admin: incluir si está inscripto explícitamente O es el profesor asignado
          if (user.rol === 'admin' && myId) {
            const inscritos = c.estudiantes_inscritos || [];
            if (inscritos.includes(myId)) return true;
            if (c.profesor_id === myId) return true;
          }
          return false;
        });
        setClases(filtered);
      } catch (e) {
        console.error('Capacitaciones widget error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, user]);

  if (loading || clases.length === 0) return null;

  return (
    <div className="mb-6" data-testid="capacitaciones-profesor-widget">
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className="text-xl">🎓</span>
        <h2 className="text-lg font-bold text-gray-800">Mis Capacitaciones Activas</h2>
        <Badge className="bg-green-600 text-white">{clases.length}</Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {clases.map((clase) => (
          <CapacitacionCard
            key={clase.id}
            clase={clase}
            onEnter={() => {
              const isAuto = ['autocapacitacion', 'autocapacitacion_profesores'].includes((clase.categoria || '').toLowerCase());
              if (isAuto) {
                // Autocapacitación: ir directo a las pruebas/exámenes de esa clase
                navigate(`/app/pruebas-examenes?clase_id=${clase.id}`);
              } else {
                // Capacitación (con Zoom): el dashboard ya muestra el botón "Entrar a Clase"
                // del MiClaseHoyWidget con todo el flujo de Zoom — lo enviamos ahí.
                navigate('/app/dashboard');
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

const CapacitacionCard = ({ clase, onEnter }) => {
  const cat = (clase.categoria || '').toLowerCase();
  const esAuto = cat === 'autocapacitacion' || cat === 'autocapacitacion_profesores';
  // ¿El usuario actual DICTA esta clase (capacitador) o la ASISTE (inscripto)?
  const dicto = clase.tipo_participacion === 'profesor';

  // Etiquetas y colores combinando "rol del usuario" + "tipo de clase"
  let badgeText;
  let badgeColor;
  let borderColor;
  let gradient;
  if (dicto) {
    badgeText = esAuto ? '📚 Dicto Auto-cap.' : '📚 Dicto Capacitación';
    badgeColor = 'bg-blue-600';
    borderColor = 'border-blue-400';
    gradient = 'from-blue-500 to-purple-500';
  } else if (esAuto) {
    badgeText = '🔄 Auto-capacitación';
    badgeColor = 'bg-indigo-600';
    borderColor = 'border-indigo-400';
    gradient = 'from-indigo-500 to-purple-500';
  } else {
    badgeText = '🎓 Capacitación';
    badgeColor = 'bg-green-600';
    borderColor = 'border-green-400';
    gradient = 'from-green-500 to-emerald-500';
  }

  return (
    <Card
      className={`relative border-2 ${borderColor} shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
      data-testid={`capacitacion-card-${clase.id}`}
    >
      {(clase.banner_url || clase.imagen_url) ? (
        <div className="relative h-28 overflow-hidden">
          <img
            src={clase.banner_url || clase.imagen_url}
            alt={clase.nombre}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute top-2 right-2">
            <Badge className={`${badgeColor} text-white text-[10px]`}>{badgeText}</Badge>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-2">
            <h3 className="text-sm font-bold text-white drop-shadow-lg line-clamp-2">
              {clase.nombre}
            </h3>
          </div>
        </div>
      ) : (
        <div className={`bg-gradient-to-r ${gradient} p-4 relative`}>
          <div className="absolute top-2 right-2">
            <Badge className="bg-white/20 text-white text-[10px]">{badgeText}</Badge>
          </div>
          <h3 className="text-sm font-bold text-white line-clamp-2 mt-3">
            {clase.nombre}
          </h3>
        </div>
      )}

      <CardContent className="p-2.5 space-y-1.5">
        {/* Horario */}
        {clase.hora_inicio && clase.hora_fin && clase.hora_inicio !== '00:00' && (
          <div className="flex items-center gap-1.5 text-xs">
            <div className="bg-blue-100 rounded p-1">
              <i className="fas fa-clock text-blue-600 text-[10px]"></i>
            </div>
            <span className="font-medium text-gray-700">
              {clase.hora_inicio} - {clase.hora_fin}
            </span>
          </div>
        )}

        {/* Profesor */}
        {clase.profesor_nombre && (
          <div className="flex items-center gap-1.5 text-xs">
            <div className="bg-purple-100 rounded p-1">
              <i className="fas fa-chalkboard-teacher text-purple-600 text-[10px]"></i>
            </div>
            <span className="text-gray-700 truncate">{clase.profesor_nombre}</span>
          </div>
        )}

        {/* Días */}
        {clase.dias_semana && clase.dias_semana.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs">
            <div className="bg-amber-100 rounded p-1">
              <i className="fas fa-calendar-day text-amber-600 text-[10px]"></i>
            </div>
            <span className="text-gray-700 capitalize truncate">
              {clase.dias_semana.join(', ')}
            </span>
          </div>
        )}

        <div className="pt-1.5 border-t border-gray-100">
          {esAuto ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEnter(); }}
              className="w-full text-xs font-semibold rounded-md py-2 text-rose-900 bg-gradient-to-r from-orange-200 via-pink-200 to-rose-300 hover:from-orange-300 hover:via-pink-300 hover:to-rose-400 shadow-md hover:shadow-lg transition-all"
              data-testid={`entrar-autocapacitacion-${clase.id}`}
            >
              <i className="fas fa-graduation-cap mr-1"></i>
              Entrar a Autocapacitación
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEnter(); }}
              className={`w-full text-xs font-semibold rounded-md py-2 text-white transition-colors bg-gradient-to-r ${dicto ? 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700' : 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'} shadow-md hover:shadow-lg`}
              data-testid={`entrar-capacitacion-${clase.id}`}
            >
              <i className="fas fa-video mr-1"></i>
              Entrar a Clase
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CapacitacionesProfesorWidget;
